import type { App } from '@grafikr/components';
import Splide from '@splidejs/splide';
import { fromEvent, tap } from 'rxjs';
import { createStore, produce } from 'solid-js/store';
import { render as renderSolidJS } from 'solid-js/web';

import HeaderMobileNavigationBreadcrumb from 'components/reactive/header/navigation/breadcrumb';

import FullModal from 'utils/components/full-modal';

type BreadcrumbItem = { title: string; handle: string };

export default (node: HTMLElement, CLASSES: { HEADER_OVERLAY_ACTIVE: string }, app: App) => {
  const trigger = node.querySelector('[data-mobile-menu-trigger]');
  const container = node.querySelector('[data-target="mobile-navigation"]');

  const initLogic = () => {
    const carousel = new Splide(container.querySelector<HTMLElement>('[data-carousel]'), {
      arrows: false,
      pagination: false,
      drag: false,
      speed: 1000,
    }).mount();

    const [breadcrumbList, setBreadcrumbList] = createStore<BreadcrumbItem[]>([]);

    renderSolidJS(
      () => (
        <HeaderMobileNavigationBreadcrumb
          class="mb-5 mt-2"
          list={breadcrumbList}
          carousel={carousel}
        />
      ),
      node.querySelector('[data-breadcrumb]'),
    );

    carousel.on('moved', (active) => {
      // Remove "next" elements when going back
      carousel.remove((slide) => slide.index > active);
    });

    carousel.on('move', (active, prevActive) => {
      const realIndex = active / 2 - 1;

      // Remove next elements when going back
      setBreadcrumbList(
        produce((list) => {
          // TODO: Remove when tsconfig has strict mode
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          list.splice(realIndex + 1);
        }),
      );

      // Add transition fade effect
      carousel.Components.Slides.getAt(active).slide.classList.remove('opacity-0');
      carousel.Components.Slides.getAt(prevActive).slide.classList.add('opacity-0');
    });

    const addBreadcrumb = (title: string, handle: string) => {
      setBreadcrumbList(
        produce((list) => {
          // TODO: Remove when tsconfig has strict mode
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          list.push({ title, handle });
        }),
      );
    };

    const listClickEvent = (root: Element) =>
      fromEvent(root.querySelectorAll<HTMLElement>('[data-child]'), 'click').pipe(
        tap((event) => {
          event.preventDefault();
        }),
      );

    const handleNavigation = (event: Event) => {
      const { currentTarget: link } = event;

      if (!(link instanceof HTMLAnchorElement)) {
        return;
      }

      addBreadcrumb(link.ariaLabel ?? link.textContent.trim(), link.dataset.title);

      const list = container
        .querySelector<HTMLTemplateElement>(`template#${link.dataset.child}`)
        .content.firstElementChild.cloneNode(true) as HTMLElement;

      const listContainer = document.createElement('div');
      listContainer.classList.add('mobile-navigation__list-container');

      list.classList.add('mobile-navigation__viewport', 'h-full');
      listContainer.append(list);

      carousel.Components.Slides.add(document.createElement('li')); // Spacer element

      const wrapper = document.createElement('div');
      wrapper.classList.add('mobile-navigation__panel');
      wrapper.append(listContainer);

      let menuImages = null;

      if (breadcrumbList.length === 1) {
        menuImages = container.querySelector<HTMLTemplateElement>(
          `template#images-${breadcrumbList[0].handle}-2`,
        );
      } else if (breadcrumbList.length === 2) {
        menuImages = container.querySelector<HTMLTemplateElement>(
          `template#images-${breadcrumbList[0].handle}-${breadcrumbList[1].handle}`,
        );
      }

      if (menuImages) {
        const syncScrollCue = () => {
          const hasOverflow = list.scrollHeight > list.clientHeight + 1;
          const isAtBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

          listContainer.classList.toggle(
            'mobile-navigation__list-container--has-scroll-cue',
            hasOverflow && !isAtBottom,
          );
        };

        list.addEventListener('scroll', syncScrollCue, { passive: true });
        requestAnimationFrame(syncScrollCue);

        wrapper.append(menuImages.content.firstElementChild.cloneNode(true));
        app.mount(wrapper);
      }

      const listItem = document.createElement('li');
      listItem.classList.add('opacity-0', 'duration-1000', 'h-full');
      listItem.append(wrapper);

      carousel.Components.Slides.add(listItem);
      carousel.go(carousel.length - 1);

      listClickEvent(list).subscribe(handleNavigation);
    };

    listClickEvent(container).subscribe(handleNavigation);
  };

  const initOpenable = () => {
    const navigation = new FullModal(container.querySelector('[data-mobile-navigation]'), {
      overlay: {
        index: 20,
      },
    });

    fromEvent(trigger, 'click').subscribe(() => {
      navigation.toggle();
    });

    fromEvent(navigation.root.querySelectorAll('[data-close]'), 'click').subscribe(() => {
      navigation.close();
    });

    navigation.on('open', () => {
      node.setAttribute('data-overlay-element', 'navigation');
      node.classList.add(CLASSES.HEADER_OVERLAY_ACTIVE);
      document.body.style.overflow = 'hidden';
    });

    navigation.on('close', () => {
      node.removeAttribute('data-overlay-element');
      node.classList.remove(CLASSES.HEADER_OVERLAY_ACTIVE);
      document.body.style.overflow = null;
    });
  };

  initOpenable();
  initLogic();
};
